import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AuthForm from './AuthForm';

// The site stays open to browse. An account is asked for at the moment someone actually does
// something — generates a certificate, buys, or contributes — rather than at the front door,
// which would turn every first visit into a form.
//
// When it is asked for, sign-in happens in a pop-up on the same page, and whatever the person
// was doing carries on by itself the moment they are signed in. Sending them to /auth instead
// would lose their place (and their half-finished certificate) and make them start again.

const GateContext = createContext(null);

export function AccountGateProvider({ children }) {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState(null);
  // The action is kept outside React state so the resume below runs it exactly once.
  const pendingAction = useRef(null);

  /**
   * Runs `action` when signed in; otherwise opens the sign-in pop-up and runs `action` as soon
   * as sign-in (or sign-up) succeeds.
   *
   * @param {string} reason what the visitor was trying to do, e.g. "download your certificate"
   */
  const requireAccount = useCallback((reason, action) => {
    if (user) {
      action();
      return true;
    }
    pendingAction.current = action;
    setPrompt({ reason });
    return false;
  }, [user]);

  const close = useCallback(() => {
    pendingAction.current = null;
    setPrompt(null);
  }, []);

  // Signed in while the pop-up was open: close it and carry on with what they were doing.
  useEffect(() => {
    if (!user || !prompt) return;
    const action = pendingAction.current;
    pendingAction.current = null;
    setPrompt(null);
    // Next tick, so the page has re-rendered as signed in before the action runs.
    if (action) setTimeout(action, 0);
  }, [user, prompt]);

  // Escape closes, and the page behind does not scroll while the pop-up is open.
  useEffect(() => {
    if (!prompt) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [prompt, close]);

  const value = useMemo(() => ({ requireAccount, signedIn: Boolean(user) }), [requireAccount, user]);

  return (
    <GateContext.Provider value={value}>
      {children}
      {prompt && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Sign in to continue"
          className="ma-gate-backdrop"
          onClick={close}
        >
          <div className="ma-gate-card" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="ma-gate-close" onClick={close} aria-label="Close">×</button>
            <AuthForm reason={prompt.reason} compact />
          </div>
          <style>{`
            .ma-gate-backdrop {
              position: fixed; inset: 0; z-index: 2000;
              display: flex; align-items: center; justify-content: center;
              padding: 16px; background: rgba(12,7,4,0.72); backdrop-filter: blur(4px);
              overflow-y: auto;
            }
            .ma-gate-card {
              position: relative; width: 100%; max-width: 440px;
              max-height: calc(100vh - 32px); overflow-y: auto;
              background: #FFFFFF; border-radius: 16px;
              border: 1px solid rgba(201,165,88,0.45);
              box-shadow: 0 24px 60px rgba(0,0,0,0.45);
              padding: 30px 26px 26px;
              animation: maGateIn 0.25s ease-out;
            }
            .ma-gate-close {
              position: absolute; top: 10px; right: 12px; width: 36px; height: 36px;
              border: none; background: none; cursor: pointer;
              font-size: 26px; line-height: 1; color: #8B6914;
            }
            @keyframes maGateIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
            @media (prefers-reduced-motion: reduce) { .ma-gate-card { animation: none; } }
          `}</style>
        </div>
      )}
    </GateContext.Provider>
  );
}

export function useAccountGate() {
  const context = useContext(GateContext);
  if (!context) {
    throw new Error('useAccountGate must be used inside an AccountGateProvider');
  }
  return context;
}
